/* eslint-disable react-hooks/exhaustive-deps */
import _ from 'lodash';
import React, { forwardRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import Slider, { Settings } from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import useToggleClass from '../hooks/useToggleClass';
import ClickableItemWrapper from './CarouselClickableItem';
import Icons from './Icons';
import Loader from './Loader';

interface ICarouselProps {
  children: any;
  loading: boolean;
  scrollOnWheel?: boolean;
}

interface IArrowProps {
  arrowClassName?: string;
}

interface IArrowClickProps {
  handleOnClick?: () => void;
}

type CarouselPros = ICarouselProps & IArrowProps & Settings;

const NextArrow = (props: IArrowProps & IArrowClickProps) => {
  const arrowRef = React.useRef(null);
  const { arrowClassName, handleOnClick } = props;
  const { addClass } = useToggleClass();

  React.useEffect(() => {
    if (arrowClassName) addClass(arrowClassName, arrowRef);
  }, []);

  return (
    <div className="carousel-arrow next" onClick={handleOnClick} ref={arrowRef}>
      <Icons.CarouselNext />
    </div>
  );
};

const PrevArrow = (props: IArrowProps & IArrowClickProps) => {
  const arrowRef = React.useRef(null);
  const { addClass } = useToggleClass();
  const { arrowClassName, handleOnClick } = props;

  React.useEffect(() => {
    if (arrowClassName) addClass(arrowClassName, arrowRef);
  }, []);

  return (
    <div className="carousel-arrow prev" onClick={handleOnClick} ref={arrowRef}>
      <Icons.CarouselPrevious />
    </div>
  );
};

function useForwardedRef<T>(ref: React.ForwardedRef<T>) {
  const innerRef = React.useRef<T>(null);
  React.useEffect(() => {
    if (!ref) return;
    if (typeof ref === 'function') {
      ref(innerRef.current);
    } else {
      ref.current = innerRef.current;
    }
  });

  return innerRef;
}

// eslint-disable-next-line react/display-name
const Carousel = forwardRef<Slider, CarouselPros>(
  function Carousel(props, ref) {
    const sliderRef = useForwardedRef(ref);
    const [isHovered, setIsHovered] = React.useState(false);
    const [isSwiping, setIsSwiping] = React.useState(false);
    const [isClicked, setIsClicked] = React.useState(false);
    const [y, setY] = React.useState<number | null>(null);
    const { children, loading, scrollOnWheel, arrowClassName, className } =
      props;

    const finalClassName = `carousel ${className ?? ''}`.trim();

    const onWheelSlider = _.debounce((e: WheelEvent) => {
      if (!sliderRef.current) return;

      if (e.deltaY > 0) {
        sliderRef.current.slickNext();
      } else if (e.deltaY < 0) {
        sliderRef.current.slickPrev();
      }
    }, 100);

    const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
      if (isHovered) {
        onWheelSlider(e as any);
      }
    };

    const handleMouseMove = (e: React.MouseEvent<any>) => {
      if (isClicked && e.clientY !== y) {
        setIsSwiping(true);
      }
    };

    const handleMouseDown = (e: React.MouseEvent<any>) => {
      setIsClicked(true);
      setY(e.clientY);
    };

    const handleMouseUp = () => {
      setIsClicked(false);
      setIsSwiping(false);
    };

    React.useEffect(() => {
      const handleDocumentScroll = (e: any) => {
        handleWheel(e);
        if (isHovered) {
          e.preventDefault();
        }
      };

      document.addEventListener('wheel', handleDocumentScroll, {
        passive: false,
      });

      return () => {
        document.removeEventListener('wheel', handleDocumentScroll);
      };
    }, [isHovered]);

    let carousel = (
      <div
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
      >
        <Loader loading={loading} loader={<Skeleton />}>
          <Slider
            {..._.omit(props, [
              'children',
              'scrollOnWheel',
              'arrowClassName',
              'nextArrow',
              'prevArrow',
              'loading',
            ])}
            className={finalClassName}
            nextArrow={
              <NextArrow
                arrowClassName={arrowClassName}
                handleOnClick={() => sliderRef.current?.slickNext()}
              />
            }
            prevArrow={
              <PrevArrow
                arrowClassName={arrowClassName}
                handleOnClick={() => sliderRef.current?.slickPrev()}
              />
            }
            ref={sliderRef}
          >
            {React.Children.map(children, child => (
              <ClickableItemWrapper isSwiping={isSwiping && isClicked}>
                {React.cloneElement(child as React.ReactElement<any>)}
              </ClickableItemWrapper>
            ))}
          </Slider>
        </Loader>
      </div>
    );

    carousel = scrollOnWheel
      ? React.cloneElement(carousel as React.ReactElement, {
          onMouseEnter: () => setIsHovered(true),
          onMouseLeave: () => setIsHovered(false),
        })
      : carousel;

    return <>{carousel}</>;
  },
);

Carousel.defaultProps = { scrollOnWheel: false };

export default Carousel;
